import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, X } from "lucide-react";
import {
  clearNavbarNotificationsForCurrentUser,
  dismissNavbarNotification,
  getNotificationsForCurrentUser,
} from "../../utils/navbarNotifications";
import { homeService } from "../../services/api";
import { socket } from "../../services/websocket";
import { getAuthJson } from "../../utils/authStorage";
import StructureReimbSyncFailReportModal from "./StructureReimbSyncFailReportModal";

function formatTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function mergeNotifications(serverItems, localItems) {
  const byKey = new Map();
  (localItems || []).forEach((n) => {
    byKey.set(`local:${n.id}`, n);
  });
  (serverItems || []).forEach((n) => {
    byKey.set(`srv:${n.id}`, {
      ...n,
      id: n.id,
      fromServer: true,
    });
  });
  return [...byKey.values()].sort(
    (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
  );
}

export default function NavbarNotifications({ darkMode = false }) {
  const [open, setOpen] = useState(false);
  const [serverItems, setServerItems] = useState([]);
  const [localTick, setLocalTick] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [detailItem, setDetailItem] = useState(null);
  const btnRef = useRef(null);

  const refreshLocal = useCallback(() => {
    setLocalTick((t) => t + 1);
  }, []);

  const refreshServer = useCallback(async () => {
    try {
      const res = await homeService.listNotifications();
      setServerItems(Array.isArray(res?.data) ? res.data : []);
    } catch {
      /* keep last */
    }
  }, []);

  const refresh = useCallback(() => {
    refreshLocal();
    refreshServer();
  }, [refreshLocal, refreshServer]);

  const updateDropdownPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    const onSocketNotify = (payload) => {
      try {
        const me = getAuthJson("currentUser");
        const myId = me?.id ?? me?.userId;
        const myCompanyId = me?.companyId ?? me?.company_id;
        if (
          payload?.companyId != null &&
          myCompanyId != null &&
          Number(payload.companyId) !== Number(myCompanyId)
        ) {
          return;
        }
        if (
          Array.isArray(payload?.userIds) &&
          payload.userIds.length &&
          myId != null &&
          !payload.userIds.map(Number).includes(Number(myId))
        ) {
          return;
        }
      } catch {
        /* ignore */
      }
      refreshServer();
      refreshLocal();
    };
    window.addEventListener("hrms:navbar-notification", onUpdate);
    window.addEventListener("storage", onUpdate);
    window.addEventListener("admin:leave-request-changed", onUpdate);
    if (socket?.on) {
      socket.on("user-notification", onSocketNotify);
      socket.on("leave-request-updated", onSocketNotify);
      socket.on("workflow-desk-updated", onSocketNotify);
    }
    const poll = setInterval(() => refreshServer(), 30000);
    return () => {
      window.removeEventListener("hrms:navbar-notification", onUpdate);
      window.removeEventListener("storage", onUpdate);
      window.removeEventListener("admin:leave-request-changed", onUpdate);
      if (socket?.off) {
        socket.off("user-notification", onSocketNotify);
        socket.off("leave-request-updated", onSocketNotify);
        socket.off("workflow-desk-updated", onSocketNotify);
      }
      clearInterval(poll);
    };
  }, [refresh, refreshServer, refreshLocal]);

  useEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    refreshServer();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [open, updateDropdownPosition, refreshServer]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-portal-navbar-notifications]")) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const localItems = getNotificationsForCurrentUser();
  // localTick forces re-read when local storage changes
  void localTick;
  const items = mergeNotifications(serverItems, localItems);
  const count = items.length;

  const handleDismiss = async (item) => {
    if (item?.fromServer || (typeof item?.id === "number")) {
      try {
        await homeService.dismissNotification(item.id);
        setServerItems((prev) => prev.filter((n) => Number(n.id) !== Number(item.id)));
      } catch {
        /* ignore */
      }
      return;
    }
    dismissNavbarNotification(item.id);
    refreshLocal();
  };

  const handleClearAll = async () => {
    clearNavbarNotificationsForCurrentUser();
    try {
      await homeService.clearNotifications();
      setServerItems([]);
    } catch {
      /* ignore */
    }
    refreshLocal();
  };

  const toggleOpen = () => {
    if (!open) updateDropdownPosition();
    setOpen((v) => !v);
  };

  const dropdownPanel =
    open &&
    createPortal(
      <div
        data-portal-navbar-notifications
        className={`fixed z-[var(--ds-z-dropdown)] w-[min(92vw,22rem)] overflow-hidden rounded-lg border shadow-xl ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
        }`}
        style={{
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
        }}
      >
        <div
          className={`flex items-center justify-between border-b px-3 py-2.5 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
        >
          <span className="text-sm font-semibold">Notifications</span>
          {count > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className={`text-[11px] font-medium underline-offset-2 hover:underline ${
                darkMode ? "text-[hsl(var(--ds-accent-fg))]" : "text-[hsl(var(--ds-secondary))]"
              }`}
            >
              Clear all
            </button>
          )}
        </div>

        <ul className="max-h-[min(60vh,320px)] overflow-y-auto">
          {count === 0 ? (
            <li
              className={`px-3 py-6 text-center text-xs ${
                darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              No notifications
            </li>
          ) : (
            items.map((item) => (
              <li
                key={item.fromServer ? `s-${item.id}` : `l-${item.id}`}
                className={`flex gap-2 border-b px-3 py-2.5 last:border-b-0 ${
                  darkMode ? "border-[hsl(var(--ds-border))]/80" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    if (
                      item.type === "structure_reimb_sync" ||
                      (item.meta && Array.isArray(item.meta.errors) && item.meta.errors.length)
                    ) {
                      setDetailItem(item);
                      setOpen(false);
                    }
                  }}
                >
                  <p className="text-xs font-semibold leading-snug">{item.title}</p>
                  <p
                    className={`mt-0.5 text-[11px] leading-snug ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {item.message}
                  </p>
                  {(item.type === "structure_reimb_sync" ||
                    (item.meta?.errors || []).length > 0) && (
                    <p className="mt-1 text-[10px] font-medium text-[hsl(var(--ds-primary))]">
                      View who failed and why
                    </p>
                  )}
                  <p
                    className={`mt-1 text-[10px] ${
                      darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    {formatTime(item.createdAt)}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => handleDismiss(item)}
                  className={`mt-0.5 shrink-0 rounded p-1 ${
                    darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-surface))]/10 hover:text-[hsl(var(--ds-fg))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  }`}
                  aria-label="Dismiss notification"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]/40 ${
          darkMode
            ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
        }`}
        title="Notifications"
        aria-label={`Notifications${count ? `, ${count} unread` : ""}`}
        aria-expanded={open}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-[hsl(var(--ds-primary))] px-1 text-[10px] font-bold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {dropdownPanel}
      <StructureReimbSyncFailReportModal
        open={!!detailItem}
        title={
          (detailItem?.meta?.failed || 0) > 0
            ? `Salary structure sync — ${detailItem.meta.failed} failed`
            : detailItem?.title || "Structure sync — failed employees"
        }
        meta={{
          updated: detailItem?.meta?.updated,
          skipped: detailItem?.meta?.skipped,
          failed: detailItem?.meta?.failed,
          effectiveFrom: detailItem?.meta?.effectiveFrom,
          total: detailItem?.meta?.total,
          failedSummary: detailItem?.meta?.failedSummary,
        }}
        errors={detailItem?.meta?.errors || []}
        onClose={() => setDetailItem(null)}
      />
    </div>
  );
}
